// src/store/rack/RackMergeStore.ts
import { MODALS_ENUM } from '@constants/Modals';
import { EditRack_RequestTypes, MergeRackItem_RequestTypes, MoveUnrackItem_RequestTypes, UnmergeRackItem_RequestTypes, UpdateRackBLI_RequestTypes } from '@mobx/api/locations';
import type { IRackModal3PropsBase } from '@mobx/components/RackModal3';
import apiService, { createStore, RequestControl, withLoading } from '@mobx/services/http';
import rackMiners from '@mobx/utils/rackMiners';
import store_ from '@store';
import { IWorkEquipment } from '@store/Equipments/actionTypes';
import { action, computed, makeObservable, observable, runInAction } from 'mobx';
import { show } from 'redux-modal';
import { IMainLocationsStore, IRackStore, ItemStore, RackStore } from './interfaces';
import createItemStore from './item';
import { IDragRackItemData3, IRack, IRackItem3, IRackSection3, IUnrackItem3, RACK_SECTION_TYPE3, TypePartialRackItem3 } from './types';
import unallocatedMinersStore from './unallocated';

const createRackStore_ = (rack: IRack, mainStore: IMainLocationsStore) => (rc: RequestControl): IRackStore => {
  const mergeRackItem = apiService.createRequest(MergeRackItem_RequestTypes);
  const unmergeRackItem = apiService.createRequest(UnmergeRackItem_RequestTypes);
  const moveUnrackItem = apiService.createRequest(MoveUnrackItem_RequestTypes);
  const editRack = apiService.createRequest(EditRack_RequestTypes);
  const updateRackBLI = apiService.createRequest(UpdateRackBLI_RequestTypes);

  const self: IRackStore = {
    isLoading: false,
    isReduced: false,
    itemStores: undefined,
    rack,

    async mergeMinerCells(draggedItem: IRackItem3, draggedItemIndex: number, targetItemId: number, targetItemIndex: number) {
      if (!mainStore.activeLocation?.activeBuilding) return false;
      const buildingStore = mainStore.activeLocation?.activeBuilding;

      // Логика вызова API и обновления состояния
      const handle = rc.createRequestHandle();
      const merge = await mergeRackItem(handle, draggedItem.id, targetItemId);
      if (!merge) return false;

      runInAction(() => {
        self.rack = {...self.rack, rack_items: self.rack.rack_items.map((item, pos) => {
          if (pos === targetItemIndex) return { ...merge.Record, miner_name: draggedItem.miner_name };
          if (pos === draggedItemIndex) return { ...draggedItem, merge: merge.Record.merge };
          return item;
        })}
      });
      buildingStore.setCanUndo();
      buildingStore.invalidateMinersStore();

      return true;
    },

    async unmergeMinerCells(itemID: number) {
      if (!mainStore.activeLocation?.activeBuilding) return false;
      const buildingStore = mainStore.activeLocation?.activeBuilding;

      const r = await unmergeRackItem(rc.createRequestHandle(), itemID);
      if (!r) return false;

      buildingStore.invalidateMinersStore();
      return true;
    },

    async addItemEquipment(askUser: boolean, itemId: number, itemIndex: number, rotateEq: IWorkEquipment): Promise<boolean> {
      // TODO:

      /**
        dispatch(
          show(MODALS_ENUM.CONFIRM_MODAL, {
            title: formatMessage({
              id: "location.rack.rack_item.back.equipment.sure_change",
            }),
            onConfirm() {
              applyNewEquipment();
            },
            onCancel() {
              setRotateEq(prevEq.current);
            },
          })
        );

        // here applyNewEquipment:
        
        const res = await enqueue(
        new AddEquipmentToRackItem(item.id.toString(), rotateEq.id),
          { onLoadingStatusChanged: onLoading }
        );
        dispatchRack({
          type: RackActions.SET_EQ_TO_ITEM,
          payload: { index: props.itemStore.itemIndex, eq: rotateEq },
        });
      */
      return false;
    },

    placeAllocatedRackItem(index: number, item: IRackItem3, data: IDragRackItemData3) {
      self.rack = {...self.rack, rack_items: self.rack.rack_items.map((it, idx) => {
        if (idx !== index) return it;
        return item;
      })};

      if (self.itemStores !== undefined) {
        self.itemStores = self.itemStores.map((s, idx) => {
          if (idx !== index) return s;
          return createItemStore(item, idx, mainStore, self);
        })
      }

      mainStore.addMinersLog(data.miner_id, data.miner_name, item.miner_name);
    },

    removeItem(id: number) {
      let newItem: TypePartialRackItem3 | undefined = undefined;
      let newIdx: number | undefined = undefined;
      self.rack = {...self.rack, rack_items: self.rack.rack_items.map((item, idx) => {
        if (item.id !== id) return item;
        newItem = {
          id: item.id,
          x: item.x,
          y: item.y,
          pos: RACK_SECTION_TYPE3.EMPTY,
        };
        newIdx = idx;
        return newItem;
      })};

      if (newItem !== undefined && newIdx !== undefined && self.itemStores !== undefined) {
        self.itemStores = self.itemStores.map((s, idx) => {
          if (idx !== newIdx) return s;
          return createItemStore(newItem!, idx, mainStore, self);
        })
      }
    },

    async placeUnallocatedRackItem(target: ItemStore, data: IUnrackItem3) {
      const buildingStore = mainStore.activeLocation!.activeBuilding!;
      const r = await moveUnrackItem(rc.createRequestHandle(), data.id, target.item.id);
      if (!r) return false;
      r.Record.user_id = data.user_id;
      runInAction(() => {
        self.rack = {...self.rack, rack_items: self.rack.rack_items.map((item, idx) => {
          if (idx !== target.itemIndex) return item;
          return r.Record;
        })};
        if (self.itemStores !== undefined) {
          self.itemStores = self.itemStores.map((s, idx) => {
            if (idx !== target.itemIndex) return s;
            return createItemStore(r.Record, idx, mainStore, self);
          })
        }
      });
      mainStore.addMinersLog(data.id, data.miner_name, r.Record.miner_name)
      buildingStore.setCanUndo();
      unallocatedMinersStore.removeMiner(data.id);
      return true;
    },

    setItem(index: number, data: TypePartialRackItem3) {
      self.rack = {...self.rack, rack_items: self.rack.rack_items.map((item, pos) => pos === index ? data : item)};
    },

    setRackNum(num: string) {
      self.rack = {...self.rack, rack_id: num};
    },

    setRack(r: IRack) {
      self.rack = r;
    },

    get isDeletable() {
      const d = self.rack.rack_items.findIndex((item) => {
        if (item.pos === RACK_SECTION_TYPE3.MINER) return true;
      });
      return d < 0;      
    },

    setReduced(v) {
      self.isReduced = v;
    },

    showRackModalForEdit() {
      const update = (items: TypePartialRackItem3[], type: [number, number], bli?: string) => {
        self.setRack({
          id: self.rack.id,
          rack_type: type,
          rack_items: items,
          rack_id: bli ?? self.rack.rack_id,
        });
        if (bli === undefined) return;
        self.updateRackBLI(+bli);
      }

      const hasMiners = rackMiners(self.rack).length > 0;
      const props: IRackModal3PropsBase = {
        rackStore: self as RackStore,
        otherRackBLIs: hasMiners ? undefined : new Set(
          Array.from(mainStore.activeLocation?.activeBuilding?.racks.values() ?? [])
          .filter(r => r.id !== self.rack.id)
          .map(r => r.rack_id)
        ),
        update,
      };

      store_()?.dispatch(
        show(MODALS_ENUM.RACK_MODAL_MOBX, props)
      );      
    },

    async editRack(sections: IRackSection3[], width: number, height: number) {
      const res = await editRack(rc.createRequestHandle(), self.rack.id, sections, width, height);
      if (!res) return;
      return res.Record;
    },

    createItemStores() {
      self.itemStores = self.rack.rack_items.map((item, i) => createItemStore(item, i, mainStore, self))
    },

    deleteItemStores() {
      self.itemStores = undefined;
    },

    async updateRackBLI(bli: number) { // используем BLI (business level identifier) вместо rack_id во избежание путаницы
      return !!await withLoading(self, updateRackBLI(rc.createRequestHandle(), self.rack.id, bli));
    }
  };

  return makeObservable(self, {
    isLoading: observable,
    isReduced: observable,
    rack: observable,
    itemStores: observable,
    isDeletable: computed,

    mergeMinerCells: action,
    unmergeMinerCells: action,
    placeAllocatedRackItem: action,
    removeItem: action,
    placeUnallocatedRackItem: action,
    addItemEquipment: action,
    setItem: action,
    setRackNum: action,
    setRack: action,
    setReduced: action,
    showRackModalForEdit: action,
    editRack: action,
    createItemStores: action,
    deleteItemStores: action,
  });
};

const createRackStore = (rack: IRack, mainStore: IMainLocationsStore) => createStore(createRackStore_(rack, mainStore));
export default createRackStore;